import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Chip,
    Avatar,
    Typography,
    TextField,
    CircularProgress,
    IconButton,
} from '@mui/material';
import { ASSIGN_UPERCUT_TO_JOB_OFFER, REMOVE_UPERCUT_FROM_JOB_OFFER } from '@gql/jobs';
import { gql } from '@apollo/client';

const GET_UPERCUT_USERS = gql`
    query GetUpercuts($limit: Int, $offset: Int) {
        getUpercuts(limit: $limit, offset: $offset) {
            upercuts {
                _id
                firstName
                lastName
                email
                color
            }
            total
            hasMore
        }
    }
`;

interface UpercutUser {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    color?: string;
}

interface UpercutAssigneeManagerProps {
    open: boolean;
    onClose: () => void;
    jobOfferId: string;
    currentAssignees?: Array<{ _id: string; assignedAt: string }>;
}

const UpercutAssigneeManager: React.FC<UpercutAssigneeManagerProps> = ({
    open,
    onClose,
    jobOfferId,
    currentAssignees = [],
}) => {
    const [assignUpercut, { loading: assignLoading }] = useMutation(ASSIGN_UPERCUT_TO_JOB_OFFER);
    const [removeUpercut, { loading: removeLoading }] = useMutation(REMOVE_UPERCUT_FROM_JOB_OFFER);
    const [fetchUpercuts, { data: upercutsData, loading: upercutsLoading }] = useLazyQuery(GET_UPERCUT_USERS);

    const [searchTerm, setSearchTerm] = useState('');
    const [localAssignees, setLocalAssignees] = useState<Array<{ _id: string; assignedAt: string }>>([]);

    useEffect(() => {
        if (open) {
            setLocalAssignees(currentAssignees);
            fetchUpercuts({ variables: { limit: 100, offset: 0 } });
            setSearchTerm('');
        }
    }, [open, currentAssignees, fetchUpercuts]);

    const assignedIds = new Set(localAssignees.map(a => a._id));

    const upercuts: UpercutUser[] = upercutsData?.getUpercuts?.upercuts || [];

    const filteredUpercuts = searchTerm
        ? upercuts.filter(
              u =>
                  `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  u.email.toLowerCase().includes(searchTerm.toLowerCase()),
          )
        : upercuts;

    const handleAssign = useCallback(
        async (upercutId: string) => {
            try {
                const { data } = await assignUpercut({
                    variables: { jobOfferId, upercutIds: [upercutId] },
                    updateQueries: {
                        GetJobOffersPro: (prev, { mutationResult }) => {
                            const updatedJob = mutationResult.data.assignUpercutToJobOffer;
                            if (!prev?.getJobOffersPro?.docs) return prev;
                            return {
                                getJobOffersPro: {
                                    ...prev.getJobOffersPro,
                                    docs: prev.getJobOffersPro.docs.map((job: any) =>
                                        job._id === updatedJob._id
                                            ? { ...job, upercutAssignees: updatedJob.upercutAssignees }
                                            : job,
                                    ),
                                },
                            };
                        },
                        GetJobOffersProUpercut: (prev, { mutationResult }) => {
                            const updatedJob = mutationResult.data.assignUpercutToJobOffer;
                            if (!prev?.getJobOffersUpercut?.docs) return prev;
                            return {
                                getJobOffersUpercut: {
                                    ...prev.getJobOffersUpercut,
                                    docs: prev.getJobOffersUpercut.docs.map((job: any) =>
                                        job._id === updatedJob._id
                                            ? { ...job, upercutAssignees: updatedJob.upercutAssignees }
                                            : job,
                                    ),
                                },
                            };
                        },
                    },
                });
                if (data?.assignUpercutToJobOffer?.upercutAssignees) {
                    setLocalAssignees(data.assignUpercutToJobOffer.upercutAssignees);
                }
            } catch (err) {
                console.error('Failed to assign upercut user:', err);
            }
        },
        [jobOfferId, assignUpercut],
    );

    const handleRemove = useCallback(
        async (upercutId: string) => {
            try {
                const { data } = await removeUpercut({
                    variables: { jobOfferId, upercutIds: [upercutId] },
                    updateQueries: {
                        GetJobOffersPro: (prev, { mutationResult }) => {
                            const updatedJob = mutationResult.data.removeUpercutFromJobOffer;
                            if (!prev?.getJobOffersPro?.docs) return prev;
                            return {
                                getJobOffersPro: {
                                    ...prev.getJobOffersPro,
                                    docs: prev.getJobOffersPro.docs.map((job: any) =>
                                        job._id === updatedJob._id
                                            ? { ...job, upercutAssignees: updatedJob.upercutAssignees }
                                            : job,
                                    ),
                                },
                            };
                        },
                        GetJobOffersProUpercut: (prev, { mutationResult }) => {
                            const updatedJob = mutationResult.data.removeUpercutFromJobOffer;
                            if (!prev?.getJobOffersUpercut?.docs) return prev;
                            return {
                                getJobOffersUpercut: {
                                    ...prev.getJobOffersUpercut,
                                    docs: prev.getJobOffersUpercut.docs.map((job: any) =>
                                        job._id === updatedJob._id
                                            ? { ...job, upercutAssignees: updatedJob.upercutAssignees }
                                            : job,
                                    ),
                                },
                            };
                        },
                    },
                });
                if (data?.removeUpercutFromJobOffer?.upercutAssignees) {
                    setLocalAssignees(data.removeUpercutFromJobOffer.upercutAssignees);
                }
            } catch (err) {
                console.error('Failed to remove upercut user:', err);
            }
        },
        [jobOfferId, removeUpercut],
    );

    const assignedUsers = upercuts.filter(u => assignedIds.has(u._id));
    const unassignedUsers = filteredUpercuts.filter(u => !assignedIds.has(u._id));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Gérer l'équipe Upercut
                <IconButton size="small" onClick={onClose}>
                    <i className="fi fi-br-cross"></i>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ my: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Membres assignés ({assignedUsers.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {assignedUsers.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Aucun membre Upercut assigné
                            </Typography>
                        ) : (
                            assignedUsers.map(user => (
                                <Chip
                                    key={user._id}
                                    avatar={
                                        <Avatar sx={{ bgcolor: user.color || '#1976d2' }}>
                                            {user.firstName[0]}
                                            {user.lastName[0]}
                                        </Avatar>
                                    }
                                    label={`${user.firstName} ${user.lastName}`}
                                    onDelete={() => handleRemove(user._id)}
                                    disabled={removeLoading}
                                    sx={{ '& .MuiChip-avatar': { width: 24, height: 24 } }}
                                />
                            ))
                        )}
                    </Box>
                </Box>

                <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Ajouter un membre
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Rechercher par nom ou email..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </Box>

                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {upercutsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        unassignedUsers.map(user => (
                            <Box
                                key={user._id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 1,
                                    px: 1,
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                                onClick={() => handleAssign(user._id)}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: user.color || '#1976d2', fontSize: 14 }}>
                                    {user.firstName[0]}
                                    {user.lastName[0]}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2">
                                        {user.firstName} {user.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {user.email}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    disabled={assignLoading}
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleAssign(user._id);
                                    }}>
                                    <i className="fi fi-br-plus"></i>
                                </IconButton>
                            </Box>
                        ))
                    )}
                    {!upercutsLoading && unassignedUsers.length === 0 && searchTerm && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Aucun résultat
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpercutAssigneeManager;
